/**
 * Addressing the tile grid.
 *
 * All access goes through these helpers rather than raw index arithmetic, because
 * two rules must be applied consistently everywhere and are easy to forget:
 *
 *   - Columns wrap east-west when `wrapX` is set (the world is a cylinder).
 *   - Rows never wrap. Row -1 and row `height` are off the top and bottom of the
 *     world, and must return "no tile" rather than silently addressing another row.
 *
 * Getting this wrong produces a map that looks fine and a pathfinder that
 * occasionally walks off the north pole and reappears in the south.
 */

import type { TileGrid } from './types.js';
import { axialToOffset, offsetToAxial, hexNeighbors, type Hex } from '../hex/index.js';

/** Index into the tile planes, or -1 if the position is off the map. */
export function tileIndex(grid: TileGrid, col: number, row: number): number {
  if (row < 0 || row >= grid.height) return -1;
  let c = col;
  if (grid.wrapX) {
    c = ((c % grid.width) + grid.width) % grid.width;
  } else if (c < 0 || c >= grid.width) {
    return -1;
  }
  return row * grid.width + c;
}

/** Index for an axial hex, or -1 if off the map. */
export function hexIndex(grid: TileGrid, h: Hex): number {
  const { col, row } = axialToOffset(h);
  return tileIndex(grid, col, row);
}

export function inBounds(grid: TileGrid, col: number, row: number): boolean {
  return tileIndex(grid, col, row) !== -1;
}

/** Offset coordinates for a plane index. Inverse of tileIndex. */
export function indexToOffset(grid: TileGrid, index: number): { col: number; row: number } {
  return { col: index % grid.width, row: Math.floor(index / grid.width) };
}

/** Axial coordinates for a plane index. */
export function indexToHex(grid: TileGrid, index: number): Hex {
  const { col, row } = indexToOffset(grid, index);
  return offsetToAxial(col, row);
}

export function tileCount(grid: TileGrid): number {
  return grid.width * grid.height;
}

/**
 * Plane indices of the up-to-six neighbours of a tile, skipping any that fall off
 * the map. Returned in HEX_DIRECTIONS order with off-map entries removed, so the
 * array length varies between 3 (a pole corner) and 6.
 *
 * Allocates. For per-tile work over the whole map — map generation, influence maps,
 * pathfinding — use `neighborsByDirection` with a reused buffer instead.
 */
export function neighborIndices(grid: TileGrid, index: number): number[] {
  const h = indexToHex(grid, index);
  const out: number[] = [];
  for (const n of hexNeighbors(h)) {
    const i = hexIndex(grid, n);
    if (i !== -1) out.push(i);
  }
  return out;
}

/**
 * Fill `out` (length 6) with each neighbour's plane index in HEX_DIRECTIONS order,
 * writing -1 where the neighbour is off the map. Allocation-free, and the direction
 * is preserved as the array position — which rivers and unit facing both need.
 *
 * Works in offset space directly rather than converting to axial and back, because
 * this is the hottest addressing routine in the engine.
 */
export function neighborsByDirection(grid: TileGrid, index: number, out: Int32Array): void {
  const col = index % grid.width;
  const row = (index - col) / grid.width;
  // On odd-r offset storage, odd rows are shifted half a hex east, so the column
  // offsets of the four diagonal neighbours differ between odd and even rows.
  const shift = row & 1 ? 0 : -1;

  out[0] = tileIndex(grid, col + 1, row); // E
  out[1] = tileIndex(grid, col + shift + 1, row - 1); // NE
  out[2] = tileIndex(grid, col + shift, row - 1); // NW
  out[3] = tileIndex(grid, col - 1, row); // W
  out[4] = tileIndex(grid, col + shift, row + 1); // SW
  out[5] = tileIndex(grid, col + shift + 1, row + 1); // SE
}

/** The opposite of a hex direction: the way back. */
export function oppositeDirection(direction: number): number {
  return (direction + 3) % 6;
}

/**
 * Allocate an empty grid. Every plane starts zeroed, which means terrain id 0 —
 * so content must declare ocean first, and the content loader asserts that.
 */
export function createGrid(width: number, height: number, wrapX: boolean): TileGrid {
  if (width < 8 || height < 8) throw new Error(`Grid too small: ${width}×${height}`);
  const n = width * height;
  const owner = new Int16Array(n).fill(-1);
  const cultureOwner = new Int16Array(n).fill(-1);
  const workedBy = new Int32Array(n).fill(-1);
  return {
    width,
    height,
    wrapX,
    terrain: new Uint8Array(n),
    feature: new Uint8Array(n),
    resource: new Uint8Array(n),
    improvement: new Uint8Array(n),
    road: new Uint8Array(n),
    elevation: new Uint8Array(n),
    rivers: new Uint8Array(n),
    owner,
    cultureOwner,
    workedBy,
  };
}
